import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { campaignAPI, contactAPI, type Campaign } from '../services/api';
import { channelOf, CHANNEL_META, channelVars, type Channel } from '../utils/channel';
import { fmt, pct } from '../utils/format';
import Icon, { type IconName } from '../components/Icon';
import StatTile from '../components/ui/StatTile';
import StatusBadge from '../components/ui/StatusBadge';
import ChannelBubble from '../components/ui/ChannelBubble';
import Sparkline from '../components/ui/Sparkline';
import { useT } from '../i18n/LanguageContext';

interface QuickAction {
  channel: Channel | null;
  icon: IconName;
  title: string;
  sub: string;
  to: string;
}

const Dashboard: React.FC = () => {
  const t = useT();
  const navigate = useNavigate();

  const QUICK_ACTIONS: QuickAction[] = [
    { channel: 'email', icon: 'mail', title: t('Email kampanja', 'Email campaign'), sub: t('Newsletter, najava', 'Newsletter, announcement'), to: '/campaigns/new?channel=email' },
    { channel: 'sms', icon: 'sms', title: t('SMS kampanja', 'SMS campaign'), sub: t('Kratko i direktno', 'Short and direct'), to: '/campaigns/new?channel=sms' },
    { channel: 'viber', icon: 'viber', title: t('Viber kampanja', 'Viber campaign'), sub: t('Slika, dugme, fallback', 'Image, button, fallback'), to: '/campaigns/new?channel=viber' },
    { channel: null, icon: 'upload', title: t('Uvezi kontakte', 'Import contacts'), sub: t('Excel / CSV lista', 'Excel / CSV list'), to: '/contact-lists' },
  ];
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [contactsTotal, setContactsTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoading(true);
        const [campRes, contactRes] = await Promise.all([
          campaignAPI.getAll(),
          contactAPI.getAll({ limit: 1 }),
        ]);
        if (!active) return;
        setCampaigns(campRes.data.data || []);
        setContactsTotal(contactRes.data.data.total || 0);
        setError(null);
      } catch (e) {
        if (active) setError(t('Ne mogu da učitam podatke. Pokušajte ponovo.', 'Unable to load data. Please try again.'));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const stats = useMemo(() => {
    const totalSent = campaigns.reduce((s, c) => s + (c.sentCount || 0), 0);
    const totalFailed = campaigns.reduce((s, c) => s + (c.failedCount || 0), 0);
    const deliveryRate = pct(totalSent, totalSent + totalFailed);

    // per-channel sent/total for the performance bars
    const byChannel: Record<Channel, { sent: number; total: number; count: number }> = {
      email: { sent: 0, total: 0, count: 0 },
      sms: { sent: 0, total: 0, count: 0 },
      viber: { sent: 0, total: 0, count: 0 },
    };
    for (const c of campaigns) {
      const ch = channelOf(c);
      byChannel[ch].sent += c.sentCount || 0;
      byChannel[ch].total += c.totalRecipients || 0;
      byChannel[ch].count += 1;
    }

    // trend: sent-per-campaign in chronological order (proxy for send activity)
    const trend = [...campaigns]
      .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
      .map((c) => c.sentCount || 0)
      .slice(-14);

    return { totalSent, deliveryRate, byChannel, trend };
  }, [campaigns]);

  const recent = useMemo(
    () =>
      [...campaigns]
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .slice(0, 5),
    [campaigns]
  );

  const scheduled = useMemo(() => campaigns.filter((c) => c.status === 'scheduled'), [campaigns]);

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <div className="page-title">{t('Pregled', 'Overview')}</div>
          <div className="page-sub">{t('Pregled aktivnosti tvojih kampanja.', 'Overview of your campaign activity.')}</div>
        </div>
        <button className="btn btn-primary" onClick={() => navigate('/campaigns/new')}>
          <Icon name="plus" size={16} />
          {t('Nova kampanja', 'New campaign')}
        </button>
      </div>

      {error && (
        <div className="card card-pad" style={{ borderColor: 'var(--danger)', color: 'var(--danger-strong)' }}>
          <Icon name="alert" size={18} /> {error}
        </div>
      )}

      {loading ? (
        <div className="card card-pad muted">{t('Učitavanje…', 'Loading…')}</div>
      ) : (
        <>
          {/* metrics */}
          <div className="stat-grid">
            <StatTile icon="send" channel="email" label={t('Ukupno poslato', 'Total sent')} value={fmt(stats.totalSent)} />
            <StatTile icon="megaphone" label={t('Kampanje', 'Campaigns')} value={fmt(campaigns.length)} />
            <StatTile icon="contacts" channel="sms" label={t('Ukupno kontakata', 'Total contacts')} value={fmt(contactsTotal)} />
            <StatTile icon="checkCircle" label={t('Stopa isporuke', 'Delivery rate')} value={`${stats.deliveryRate}%`} />
          </div>

          {/* send activity trend */}
          <div className="card card-pad" style={{ marginTop: 'var(--gap)' }}>
            <div className="flex items-center justify-between wrap gap-16" style={{ marginBottom: 14 }}>
              <div className="section-title">{t('Aktivnost slanja', 'Send activity')}</div>
              <div className="flex items-center gap-16 wrap">
                {(Object.keys(CHANNEL_META) as Channel[]).map((c) => (
                  <span key={c} className="flex items-center gap-8" style={{ fontSize: 12.5, color: 'var(--text-2)', fontWeight: 600 }}>
                    <span style={{ width: 9, height: 9, borderRadius: '50%', background: channelVars(c).color }} />
                    {CHANNEL_META[c].label}
                  </span>
                ))}
              </div>
            </div>
            <Sparkline data={stats.trend} color="var(--brand-500)" height={72} />
          </div>

          <div className="dash-grid" style={{ marginTop: 'var(--gap)' }}>
            {/* left column */}
            <div className="dash-stack">
              {/* quick actions */}
              <div className="card card-pad">
                <div className="section-title" style={{ marginBottom: 16 }}>{t('Brze akcije', 'Quick actions')}</div>
                <div className="quick-grid">
                  {QUICK_ACTIONS.map((qa) => {
                    const tint = qa.channel ? channelVars(qa.channel) : { color: 'var(--brand-600)', soft: 'var(--brand-50)' };
                    return (
                      <button key={qa.title} className="quick-action" onClick={() => navigate(qa.to)}>
                        <span className="ch-bubble sm" style={{ background: tint.soft, color: tint.color }}>
                          <Icon name={qa.icon} size={16} />
                        </span>
                        <span>
                          <span className="qa-title" style={{ display: 'block' }}>{qa.title}</span>
                          <span className="qa-sub">{qa.sub}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* recent campaigns */}
              <div className="card card-pad">
                <div className="flex items-center justify-between" style={{ marginBottom: 12 }}>
                  <div className="section-title">{t('Nedavne kampanje', 'Recent campaigns')}</div>
                  <button className="btn btn-ghost btn-sm" onClick={() => navigate('/campaigns')}>
                    {t('Sve', 'All')} <Icon name="arrowRight" size={15} />
                  </button>
                </div>
                {recent.length === 0 ? (
                  <div className="empty">
                    <div className="empty-ico"><Icon name="inbox" size={30} /></div>
                    <div className="card-title">{t('Još nema kampanja', 'No campaigns yet')}</div>
                    <div className="muted" style={{ marginTop: 4 }}>{t('Napravi prvu kampanju da vidiš rezultate ovde.', 'Create your first campaign to see results here.')}</div>
                    <button className="btn btn-primary mt-16" onClick={() => navigate('/campaigns/new')}>
                      <Icon name="plus" size={16} /> {t('Nova kampanja', 'New campaign')}
                    </button>
                  </div>
                ) : (
                  recent.map((c) => {
                    const ch = channelOf(c);
                    return (
                      <div key={c.id} className="lrow" style={{ cursor: 'pointer' }} onClick={() => navigate(`/campaigns/${c.id}`)}>
                        <ChannelBubble channel={ch} size="sm" />
                        <div className="grow" style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.name}</div>
                          <div className="dim" style={{ fontSize: 12.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {c.subject || CHANNEL_META[ch].label}
                          </div>
                        </div>
                        <span className="tnum dim" style={{ fontSize: 13 }}>{fmt(c.totalRecipients)}</span>
                        <StatusBadge status={c.status} />
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* right column */}
            <div className="dash-stack">
              {/* scheduled */}
              <div className="card card-pad">
                <div className="section-title" style={{ marginBottom: 12 }}>{t('Zakazano', 'Scheduled')}</div>
                {scheduled.length === 0 ? (
                  <div className="muted" style={{ fontSize: 13.5 }}>{t('Nema zakazanih kampanja.', 'No scheduled campaigns.')}</div>
                ) : (
                  scheduled.slice(0, 4).map((c) => (
                    <div key={c.id} className="lrow" style={{ cursor: 'pointer', padding: '10px 8px' }} onClick={() => navigate(`/campaigns/${c.id}`)}>
                      <Icon name="clock" size={18} style={{ color: 'var(--warn)' }} />
                      <div className="grow" style={{ minWidth: 0, fontWeight: 600, fontSize: 13.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {c.name}
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* channel performance */}
              <div className="card card-pad">
                <div className="section-title" style={{ marginBottom: 14 }}>{t('Učinak po kanalu', 'Channel performance')}</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {(Object.keys(stats.byChannel) as Channel[]).map((ch) => {
                    const d = stats.byChannel[ch];
                    const rate = pct(d.sent, d.total);
                    return (
                      <div key={ch}>
                        <div className="flex items-center justify-between" style={{ marginBottom: 7 }}>
                          <span className={`ch-tag ${ch}`}>
                            <Icon name={CHANNEL_META[ch].icon} size={14} />
                            {CHANNEL_META[ch].label}
                          </span>
                          <span className="tnum dim" style={{ fontSize: 12.5 }}>{fmt(d.sent)} / {fmt(d.total)}</span>
                        </div>
                        <div className="progress thin">
                          <span style={{ width: `${rate}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;
